import React, { useCallback, useEffect, useState } from 'react';
import { useApiCaller } from '../../utils/ApiUtilities';
import { useError } from '../ErrorContext';
import {
    getAdminReferralsUrl,
    getAdminReferralsTopUrl,
    getReferralGrantUrl,
} from '../../utils/UrlUtilities';
import { constructReferralGrantPayload } from '../../utils/PayloadUtils';
import { itemsPerPageOptions } from '../../utils/Constants';
import { isEmpty } from '../../utils/BasicUtilities';

const STATUS_BADGE = {
    INVITED: 'bg-secondary',
    SIGNED_UP: 'bg-info text-dark',
    VERIFIED: 'bg-primary',
    PURCHASED: 'bg-success',
};

const PLAN_OPTIONS = ['STARTER', 'PRO', 'PREMIUM'];

export default function ReferralDashboard() {
    const CallApi = useApiCaller();
    const { showSuccess } = useError();

    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(50);
    const [loading, setLoading] = useState(false);
    const [pending, setPending] = useState({ q: '', status: '', date_from: '', date_to: '' });
    const [applied, setApplied] = useState({ q: '', status: '', date_from: '', date_to: '' });

    const [top, setTop] = useState({ top_by_signups: [], top_by_purchases: [] });

    // Grant-days modal.
    const [grantFor, setGrantFor] = useState(null); // referral row (uses referrer_id)
    const [grantForm, setGrantForm] = useState({ planCode: 'STARTER', days: 2, reason: '' });
    const [granting, setGranting] = useState(false);

    const fetchRows = useCallback(async () => {
        setLoading(true);
        const params = { page, limit };
        Object.entries(applied).forEach(([k, v]) => { if (!isEmpty(v)) params[k] = v; });
        const res = await CallApi({ url: getAdminReferralsUrl(params), method: 'GET', token: true });
        if (res.success) {
            setRows(res.data.referrals || []);
            setTotal(res.data.total || 0);
        }
        setLoading(false);
    }, [page, limit, applied, CallApi]);

    const fetchTop = useCallback(async () => {
        const res = await CallApi({ url: getAdminReferralsTopUrl(10), method: 'GET', token: true });
        if (res.success) setTop(res.data || { top_by_signups: [], top_by_purchases: [] });
    }, [CallApi]);

    useEffect(() => { fetchRows(); }, [fetchRows]);
    useEffect(() => { fetchTop(); }, [fetchTop]);

    const handleGrant = async () => {
        if (!grantFor || !(Number(grantForm.days) > 0)) return;
        setGranting(true);
        const res = await CallApi({
            url: getReferralGrantUrl(grantFor.referrer_id),
            method: 'POST',
            payload: constructReferralGrantPayload(grantForm),
            token: true,
        });
        setGranting(false);
        if (res.success) {
            showSuccess(res.data?.detail || 'Days granted.');
            setGrantFor(null);
            setGrantForm({ planCode: 'STARTER', days: 2, reason: '' });
        }
    };

    const totalPages = Math.ceil(total / limit) || 1;

    return (
        <div className="container py-4">
            <h2 className="fw-bold mb-4" style={{ color: '#4c1d95' }}>Referral Dashboard</h2>

            {/* Top referrers */}
            <div className="row g-3 mb-4">
                <div className="col-12 col-md-6">
                    <div className="card h-100">
                        <div className="card-header fw-bold">🏆 Top by signups</div>
                        <ul className="list-group list-group-flush">
                            {top.top_by_signups.length === 0 ? (
                                <li className="list-group-item text-muted small">No conversions yet.</li>
                            ) : top.top_by_signups.map((r) => (
                                <li key={r.referrer_id} className="list-group-item d-flex justify-content-between align-items-center">
                                    <span className="small"><strong>{r.name}</strong> <span className="text-muted">{r.email}</span></span>
                                    <span className="badge bg-info text-dark">{r.signups} signed up</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
                <div className="col-12 col-md-6">
                    <div className="card h-100">
                        <div className="card-header fw-bold">💰 Top by purchases</div>
                        <ul className="list-group list-group-flush">
                            {top.top_by_purchases.length === 0 ? (
                                <li className="list-group-item text-muted small">No purchases yet.</li>
                            ) : top.top_by_purchases.map((r) => (
                                <li key={r.referrer_id} className="list-group-item d-flex justify-content-between align-items-center">
                                    <span className="small"><strong>{r.name}</strong> <span className="text-muted">{r.email}</span></span>
                                    <span className="badge bg-success">{r.purchases} bought</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="card mb-3 kp-filter"><div className="card-body row g-2 align-items-end">
                <div className="col-6 col-md-4">
                    <label className="form-label small mb-1">Search</label>
                    <input className="form-control form-control-sm" value={pending.q} onChange={(e) => setPending({ ...pending, q: e.target.value })} placeholder="referrer name/email or invited email" />
                </div>
                <div className="col-6 col-md-2">
                    <label className="form-label small mb-1">Status</label>
                    <select className="form-select form-select-sm" value={pending.status} onChange={(e) => setPending({ ...pending, status: e.target.value })}>
                        <option value="">All</option>
                        <option value="INVITED">Invited</option>
                        <option value="SIGNED_UP">Signed up</option>
                        <option value="VERIFIED">Verified</option>
                        <option value="PURCHASED">Purchased</option>
                    </select>
                </div>
                <div className="col-6 col-md-2">
                    <label className="form-label small mb-1">From</label>
                    <input type="date" className="form-control form-control-sm" value={pending.date_from} onChange={(e) => setPending({ ...pending, date_from: e.target.value })} />
                </div>
                <div className="col-6 col-md-2">
                    <label className="form-label small mb-1">To</label>
                    <input type="date" className="form-control form-control-sm" value={pending.date_to} onChange={(e) => setPending({ ...pending, date_to: e.target.value })} />
                </div>
                <div className="col-6 col-md-2">
                    <button className="btn btn-sm btn-outline-primary w-100" onClick={() => { setApplied(pending); setPage(1); }}>Filter</button>
                </div>
            </div></div>

            {/* Who-referred-who grid */}
            <div className="card"><div className="card-body p-0 table-responsive">
                <table className="table table-hover mb-0 align-middle">
                    <thead className="table-light">
                        <tr>
                            <th>Referrer</th><th>Invited</th><th>Status</th><th>Verified</th><th>Signed up</th><th>Purchased</th><th className="text-end">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={7} className="text-center text-muted py-4">Loading…</td></tr>
                        ) : rows.length === 0 ? (
                            <tr><td colSpan={7} className="text-center text-muted py-4">No referrals found.</td></tr>
                        ) : rows.map((r) => (
                            <tr key={r.referral_id}>
                                <td>
                                    <div className="fw-semibold">{r.referrer_name}</div>
                                    <div className="small text-muted">{r.referrer_email}</div>
                                </td>
                                <td className="small">
                                    <div>{r.referee_name || r.invited_email}</div>
                                    {r.referee_name && <div className="text-muted">{r.invited_email}</div>}
                                </td>
                                <td><span className={`badge ${STATUS_BADGE[r.status] || 'bg-secondary'}`}>{r.status}</span></td>
                                <td>{r.email_verified ? <span className="text-success small">✓</span> : <span className="text-muted small">—</span>}</td>
                                <td className="small text-muted">{r.signup_at ? new Date(r.signup_at).toLocaleDateString() : '—'}</td>
                                <td className="small text-muted">{r.purchase_at ? new Date(r.purchase_at).toLocaleDateString() : '—'}</td>
                                <td className="text-end">
                                    <button className="btn btn-sm btn-outline-primary" onClick={() => { setGrantFor(r); setGrantForm({ planCode: 'STARTER', days: 2, reason: `Manual referral bonus for ${r.referrer_name}` }); }}>Grant days</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div></div>

            {/* Pagination */}
            <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="d-flex align-items-center gap-2">
                    <label className="small mb-0">Items per page:</label>
                    <select className="form-select form-select-sm w-auto" value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}>
                        {itemsPerPageOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <button className="btn btn-sm btn-outline-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
                    <span className="small text-muted">Page {page} of {totalPages}</span>
                    <button className="btn btn-sm btn-outline-secondary" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
                </div>
            </div>

            {/* Grant-days modal */}
            {grantFor && (
                <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1060 }}>
                    <div className="card" style={{ width: '90%', maxWidth: 'min(480px, 94vw)' }}>
                        <div className="card-header fw-bold" style={{ background: 'linear-gradient(145deg,#7c3aed,#5b21b6)', color: '#fff' }}>
                            Grant days to {grantFor.referrer_name}
                        </div>
                        <div className="card-body">
                            <div className="row g-2">
                                <div className="col-6">
                                    <label className="form-label small mb-1">Plan</label>
                                    <select className="form-select form-select-sm" value={grantForm.planCode} onChange={(e) => setGrantForm({ ...grantForm, planCode: e.target.value })}>
                                        {PLAN_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                                    </select>
                                </div>
                                <div className="col-6">
                                    <label className="form-label small mb-1">Days</label>
                                    <input type="number" min="1" className="form-control form-control-sm" value={grantForm.days} onChange={(e) => setGrantForm({ ...grantForm, days: e.target.value })} />
                                </div>
                                <div className="col-12">
                                    <label className="form-label small mb-1">Reason</label>
                                    <input className="form-control form-control-sm" value={grantForm.reason} onChange={(e) => setGrantForm({ ...grantForm, reason: e.target.value })} />
                                </div>
                            </div>
                        </div>
                        <div className="card-footer d-flex justify-content-end gap-2">
                            <button className="btn btn-outline-secondary" onClick={() => setGrantFor(null)} disabled={granting}>Cancel</button>
                            <button className="btn btn-primary" onClick={handleGrant} disabled={granting || !(Number(grantForm.days) > 0)}>{granting ? 'Granting…' : 'Grant'}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
